import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { optionSetsByUuid, positiveOptionOf } from "../Templates/templateTree";

const OPERATOR_GLYPH = { equals: "=", not_equals: "≠" };

const selectClass =
  "appearance-none pl-2 pr-7 py-1.5 text-xs font-semibold text-slate-700 rounded-lg border border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition cursor-pointer max-w-full truncate disabled:opacity-60";

// Recommendation-rule editor for a material. Unlike the template condition
// composer (earlier-only, ANDed visibility), this is a flat list of rules with
// OR semantics: a teacher sees the material when ANY rule matches. Each rule is
// [question][is / is not][answer], where the answer options come from the
// question's own answer_option_set in the active rubric. The active rubric is
// `getActiveRubric()` output: { uuid, standards:[{number,title,questions:[{uuid,
// code,text,short_label,answer_option_set}]}], answer_option_sets:[{uuid,options}] }.
export default function RuleBuilder({ rubric, value = [], onChange, disabled }) {
  const { t } = useTranslation();

  const questions = useMemo(() => {
    const standards = [...(rubric?.standards || [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    return standards.flatMap((std) =>
      [...(std.questions || [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((q) => ({
          uuid: q.uuid,
          answerOptionSet: q.answer_option_set,
          label: `${q.code ? `${q.code} ` : ""}${q.short_label || q.text || ""}`.trim(),
        })),
    );
  }, [rubric]);

  const questionByUuid = useMemo(
    () => Object.fromEntries(questions.map((q) => [q.uuid, q])),
    [questions],
  );

  const optionSetByUuid = useMemo(
    () => optionSetsByUuid(rubric?.answer_option_sets),
    [rubric],
  );

  const optionsForQuestion = (questionUuid) => {
    const q = questionByUuid[questionUuid];
    return optionSetByUuid[q?.answerOptionSet]?.options || [];
  };

  const optionLabel = (questionUuid, optionUuid) => {
    const opt = optionsForQuestion(questionUuid).find((o) => o.uuid === optionUuid);
    return opt ? opt.short_label || opt.label : "?";
  };

  const defaultExpectedFor = (questionUuid) => {
    const positive = positiveOptionOf(optionSetByUuid[questionByUuid[questionUuid]?.answerOptionSet]);
    return positive?.uuid || optionsForQuestion(questionUuid)[0]?.uuid || "";
  };

  const addRule = () => {
    const first = questions[0];
    if (!first) return;
    onChange([
      ...value,
      {
        question: first.uuid,
        operator: "equals",
        expected_option: defaultExpectedFor(first.uuid),
      },
    ]);
  };

  const updateRule = (idx, patch) => {
    onChange(value.map((rule, i) => (i === idx ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const onQuestionChange = (idx, questionUuid) => {
    // Switching the question invalidates the old option; reset to the new
    // question's positive (or first) option.
    updateRule(idx, {
      question: questionUuid,
      expected_option: defaultExpectedFor(questionUuid),
    });
  };

  if (!questions.length) {
    return (
      <p className="text-xs text-slate-500 italic">{t("materials.noActiveRubric")}</p>
    );
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <p className="text-[11px] font-semibold text-slate-500">
          {t("materials.ruleOrHint")}
        </p>
      )}

      {value.map((rule, idx) => (
        <div
          key={idx}
          className="flex items-center gap-1.5 flex-wrap rounded-xl border border-slate-200 bg-slate-50 p-2"
        >
          {idx > 0 && (
            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">
              {t("materials.ruleOr")}
            </span>
          )}
          <select
            value={rule.question}
            disabled={disabled}
            onChange={(e) => onQuestionChange(idx, e.target.value)}
            className={selectClass}
            aria-label={t("materials.ruleQuestion")}
          >
            {questions.map((q) => (
              <option key={q.uuid} value={q.uuid}>
                {q.label}
              </option>
            ))}
          </select>
          <select
            value={rule.operator}
            disabled={disabled}
            onChange={(e) => updateRule(idx, { operator: e.target.value })}
            className={selectClass}
            aria-label={t("materials.ruleOperator")}
          >
            <option value="equals">{t("materials.ruleIs")}</option>
            <option value="not_equals">{t("materials.ruleIsNot")}</option>
          </select>
          <select
            value={rule.expected_option}
            disabled={disabled}
            onChange={(e) => updateRule(idx, { expected_option: e.target.value })}
            className={selectClass}
            aria-label={t("materials.ruleAnswer")}
          >
            {optionsForQuestion(rule.question).map((opt) => (
              <option key={opt.uuid} value={opt.uuid}>
                {opt.short_label || opt.label}
              </option>
            ))}
          </select>
          <span className="text-[11px] font-semibold text-slate-500 ml-auto" aria-hidden="true">
            {OPERATOR_GLYPH[rule.operator]} {optionLabel(rule.question, rule.expected_option)}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => removeRule(idx)}
            title={t("materials.removeRule")}
            aria-label={t("materials.removeRule")}
            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}

      <button
        type="button"
        disabled={disabled}
        onClick={addRule}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition disabled:opacity-50"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
        </svg>
        {t("materials.addRule")}
      </button>
    </div>
  );
}
